import React, {CSSProperties, lazy, ReactElement, Suspense, useEffect, useMemo, useRef, useState} from 'react';
import './Campaigns.less';
import CampaignCard from './CampaignCard';
import Auth from '@/Auth/Auth';
import util from '@/lib/util';
import {deleteCampaign, deleteImpactCampaign} from '@/store/deps/campaignDeps';
import {deleteImpactWarning, reportDeleteResult, useCascadeDeleteReady} from '@/lib/cascadeDelete';
import IconButton from '@/ui/IconButton/IconButton';
import Icons from '@/ui/IconButton/Icons';
import * as CampaignHelper from './CampaignHelper';
import * as DateUtil from '@/lib/dateutils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import StateLabel from '@/ui/StateLabel/StateLabel';
import * as CampaignActions from '@/store/actions/campaignActions';
import {connect} from 'react-redux';
import Alert from '@/lib/alert';
import i18n from '@/i18n/i18n';
import chroma from 'chroma-js';
import mathUtils from '@/lib/mathUtils';
import {getCurrentUserId} from "@/lib/picos/currentLogin";
import Loading from "@/ui/Loading";
import MarketingPageHead from "@/CRM/Marketing/MarketingPageHead";
import {WithRouter, withRouter} from "@/ui/withRouter";
import {SearchOptions} from "@/Search/Searchbar";
import {RootDispatch, RootState} from "@/@types/store";
import Campaign from "@/entities/campaign";
import {EditableTablePropertyEntry, EditableTableProps, EditableTableSumEntry} from "@/ui/EditableTable/EditableTable";
import ArrayMap from "@/lib/ArrayMap";
import {VoidCallback} from "@/@types/functions";
import {ContentBlockChildProps} from "@/ui/ContentBlock/ContentBlock";
import {componentCacheStore} from "@/hooks/useComponentCache";
import {sameText} from "@/lib/picos/textMatch";

const EditableTable = lazy(() => import('@/ui/EditableTable/EditableTable'));

export const campaignTypeIcons = new Map([
    ['Ad', Icons.AD],
    ['Agencies', Icons.BUSINESS],
    ['Bar Association', Icons.SCALES],
    ['Blog', Icons.BLOG],
    ['Bulletins', Icons.BULLETIN],
    ['Chamber of Commerce', Icons.BANK],
    ['Charity and Community Events', Icons.CHARITY],
    ['CLE', Icons.LEARN],
    ['Establishment', Icons.ESTABLISHMENT],
    ['Event', Icons.EVENT],
    ['Personal Contact', Icons.PERSON_SIGN],
    ['Leave Behind', Icons.ADDRESS_CARD],
    ['Networking Group', Icons.NETWORK],
    ['Newsletter', Icons.NEWSLETTER],
    ['Partnership', Icons.HANDSHAKE],
    ['Periodical', Icons.PERIODICAL],
    ['Podcast', Icons.PODCAST],
    ['Presentation', Icons.PRESENT],
    ['Search Engines', Icons.SEARCH],
    ['Social Media', Icons.SOCIAL],
    ['Sponsorship', Icons.SPONSORSHIP],
    ['Walk-In', Icons.WALK_IN],
    ['Website', Icons.WWW],
]);

type CampaignsState = {
    isEditing: boolean,
}

const Campaigns: React.FC<CampaignsProps> = (props) => {
    const cache = useMemo(
        () => componentCacheStore.typed<CampaignsState>('campaigns'),
        []
    );

    const [state, setState] = useState<CampaignsState>(() => {
        if (cache.exists()) {
            const cached = cache.getItem();
            if (cached) return {...cached};
        }
        return {isEditing: false};
    });

    const doResetRef = useRef(false);

    // Mirrors state.isEditing but is updated synchronously in onBeginEdit/onEndEdit.
    // The unmount cleanup must read this rather than state because pending
    // setState calls (e.g. from a cancel handler that also navigates) may not have
    // committed before unmount, leaving state.isEditing stale.
    const latestIsEditingRef = useRef<boolean>(state.isEditing);

    useEffect(() => {
        return () => {
            if (doResetRef.current) cache.resetItem();
            else cache.setItem({isEditing: latestIsEditingRef.current});
            doResetRef.current = false;
        };
    }, [cache]);

    /**
     * Get the current list of campaigns.
     */
    const getCampaigns = (): ArrayMap<Campaign> | Campaign[] => {
        if (state.isEditing) return [];
        else return props.campaigns;
    };

    // Componentdiddupdate equivalent: error notification effect
    useEffect(() => {
        getCampaigns().forEach(c => {
            if (c.fatalError && (c.fatalError.message || c.fatalError.status) && !c.errorShown) {
                const message = `Error saving campaign: '${c.name}': ${c.fatalError.message} (code ${c.fatalError.status})`;
                c.errorShown = true;
                Alert.add(new Alert(Alert.ERROR, c.id, c.addedOn, message));
            }
        });
    });

    /**
     * Is the screen small? (mobile or in a content block).
     */
    const isSmall = (): boolean => {
        return props.containerWidth <= 1100;
    };

    /**
     * Returned from add action, receives a campaign
     */
    const onAdd = (campaign: Campaign, cb: VoidCallback) => {
        const campaignClone = campaign.clone();

        campaignClone.addedBy = getCurrentUserId();
        campaignClone.addedOn = util.newHRTimestamp();
        campaignClone.status = 'C';
        campaignClone.entityType = 'campaign';
        campaignClone.persisted = false;

        props.onSaveCampaignQueue(campaignClone);

        if (typeof (cb) === 'function') cb();
    };

    const onEdit = (campaign: Campaign, cb: VoidCallback) => {
        const campaignClone = campaign.clone();

        campaignClone.addedBy = getCurrentUserId();
        campaignClone.addedOn = util.newHRTimestamp();
        campaignClone.status = 'U';
        campaignClone.persisted = false;

        props.onSaveCampaignQueue(campaignClone);

        if (typeof (cb) === 'function') cb();
    };

    const onDelete = (campaign: Campaign, cb: (cancel?: boolean) => void) => {
        const deleted = reportDeleteResult('campaign', deleteCampaign(campaign));

        if (typeof (cb) === 'function') cb(!deleted);
    };

    // OWA-930: deleting a campaign deletes its comments and documents; show the counts and wait for the first fetch.
    const confirmDeleteWarning = (campaign: Campaign) => deleteImpactWarning('campaign', deleteImpactCampaign(campaign), campaign.name);
    const cascadeReady = useCascadeDeleteReady();

    const onDuplicateFound = (campaign: Campaign) => {
        const matches = getCampaigns().filter(c => sameText(c.name, campaign.name));
        return matches.length > 0;
    };

    const getSortedCampaigns = () => {
        if (state.isEditing) return [];

        const doSearch = !state.isEditing && props.searchTerms && props.searchTerms.length > 0;

        const campaigns = !doSearch
                          ? getCampaigns()
                          : getCampaigns()
                                .filter(c => {
                                    let found = c.search(props.searchTerms, props.searchOptions);
                                    if (found) return true;

                                    const documents = props.documents.filter(doc => doc.entityType === 'campaign' && doc.entityId === c.id);
                                    if (documents) {
                                        const docs = documents.filter(doc => doc.local && (doc.contentType === 'text/plain' || doc.contentType === 'text/html'));
                                        const documentsMatch = docs.some(doc => doc.search(props.searchTerms, props.searchOptions));
                                        if (documentsMatch) return true;
                                    }

                                    return false;
                                });

        return campaigns.sort((a, b) => {
            if (a.closed === b.closed) {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            }
            else {
                const ac = a.closed ? 1 : 0;
                const bc = b.closed ? 1 : 0;
                return ac - bc;
            }
        });
    };

    /**
     * Used by CampaignCard, passed through EditableTable.props.env
     */
    const getIconLabel = (category: string) => {
        let icon = campaignTypeIcons.get(category);

        return <div className='campaign-card__category-entry'><IconButton type={icon} color='black'/>{category}</div>;
    };

    const getIcon = (campaign: Campaign) => {
        let icon = campaignTypeIcons.get(campaign.category);
        if (/facebook/i.test(campaign.name)) icon = Icons.FACEBOOK;
        else if (/google/i.test(campaign.name)) icon = Icons.GOOGLE;
        else if (/twitter/i.test(campaign.name)) icon = Icons.TWITTER;
        else if (/yelp/i.test(campaign.name)) icon = Icons.YELP;

        return <IconButton type={icon}/>;
    };

    const getHardCosts = (campaign: Campaign) => {
        return CampaignHelper.getHardCosts(campaign);
    };

    const renderHardCosts = (campaign: Campaign) => {
        return util.formatCurrency(getHardCosts(campaign));
    };

    const getLaborCosts = (campaign: Campaign) => {
        return CampaignHelper.getLaborCosts(campaign, props.tasks) as number;
    };

    const renderLaborCosts = (campaign: Campaign) => {
        return util.formatCurrency(getLaborCosts(campaign) as number);
    };

    const getRecurringCosts = (campaign: Campaign) => {
        return CampaignHelper.getRecurringCosts(campaign) as number;
    };

    const renderRecurringCosts = (campaign: Campaign) => {
        return util.formatCurrency(getRecurringCosts(campaign) as number);
    };

    const getTotalCosts = (campaign: Campaign) => {
        return CampaignHelper.getTotalCost(campaign);
    };

    const getCostPerLead = (campaign: Campaign) => {
        return CampaignHelper.getCostPerLead(campaign);
    };

    const getConversionRate = (campaign: Campaign) => {
        return CampaignHelper.getConversionRate(campaign);
    };

    const renderTotalCosts = (campaign: Campaign) => {
        return <b>{util.formatCurrency(getTotalCosts(campaign) as number)}</b>;
    };

    const renderNumLeads = (campaign: Campaign) => {
        return CampaignHelper.getLeadIds(campaign).length;
    };

    const renderRoiFlag = (totalCost: number, amount: number, _type: 'B' | 'C', campaign: Campaign, isExport: boolean): string | ReactElement => {
        const longRunning = DateUtil.subtract(new Date(), 1, 'year').toDate() > campaign.startDate;

        const renderRoiColor = () => {
            if (totalCost === 0) {
                if (amount > 0) return 'green';
                else if (longRunning) return 'orange';
                else return 'green';
            }
            else {
                const roi = (amount / totalCost);
                if (amount === 0) return 'maroon';
                else if (roi < 1) return 'maroon';
                else if (roi < 2) return 'red';
                else if (roi < 3) return 'pink';
                else if (roi < 5) return 'orange';
                else if (roi < 7) return 'green';
                else return 'darkgreen';
            }
        };

        const enoughTime = DateUtil.add(campaign.startDate, 6, 'month') < new Date();

        const roi = (amount / (totalCost || 1));
        let roiA: string | ReactElement;

        if (totalCost === 0 && amount > 0) roiA = 'Awesome';
        else if (!enoughTime) roiA = isExport || props.containerWidth >= 1200 ? 'Needs Time' :
                                     <span>Needs<br/>Time</span>;
        else if (amount > 0 && totalCost === 0) roiA = '0:1';
        else if (amount === 0 && totalCost === 0) roiA = 'Useless';
        else roiA = `${Math.floor(amount / totalCost).toFixed(0)}:1`;

        const icon = enoughTime
                     ? longRunning && amount > 0 && roi < 1 ? Icons.POO_STORM.reactIcon : Icons.FLAG_FILLED.reactIcon
                     : Icons.FLAG.reactIcon;

        if (isExport) {
            return roiA as string;
        }
        else return <span>
            <FontAwesomeIcon color={renderRoiColor()} icon={icon}/>
            <span style={{fontSize: '.85em'}}>&nbsp;{roiA}</span>
        </span>;

    };

    /**
     * Get the roi for a campaign as a ratio.
     */
    const getRoi = (campaign: Campaign, type: 'B' | 'C'): string | ReactElement => {
        const totalCost = getTotalCosts(campaign) as number;
        const {billed, collected} = CampaignHelper.getCampaignBilledAndCollected(campaign);

        return renderRoiFlag(totalCost, type === 'B' ? billed : collected, type, campaign, true);
    };

    /**
     * Sort campaigns by ROI.
     */
    const sortRoi = (campaignA: Campaign, campaignB: Campaign, type: 'B' | 'C'): number => {
        const totalCostA = getTotalCosts(campaignA);
        const totalCostB = getTotalCosts(campaignB);

        const bacA = CampaignHelper.getCampaignBilledAndCollected(campaignA);
        const bacB = CampaignHelper.getCampaignBilledAndCollected(campaignB);

        const roiA = (type === 'B' ? bacA.billed : bacA.collected) / (totalCostA as number || .0001);
        const roiB = (type === 'B' ? bacB.billed : bacB.collected) / (totalCostB as number || .0001);

        return roiA - roiB;
    };

    const renderROI = (campaign: Campaign, type: 'B' | 'C') => {
        const totalCosts = getTotalCosts(campaign) as number;
        const {billed, collected} = CampaignHelper.getCampaignBilledAndCollected(campaign);

        const flag = renderRoiFlag(totalCosts, type === 'B' ? billed : collected, type, campaign, false);

        return <div>
            {flag}
            <div style={{fontSize: '.9em'}}>{util.formatCurrency(type === 'B' ? billed : collected)}</div>
        </div>;
    };

    const renderCPL = (campaign: Campaign) => {
        const cpl = getCostPerLead(campaign);
        return util.formatCurrency(cpl);
    };

    const renderConversionRate = (campaign: Campaign) => {
        const cr = getConversionRate(campaign);
        return `${cr}%`;
    };

    const onBeginEdit = () => {
        latestIsEditingRef.current = true;
        setState({isEditing: true});
    };

    const onEndEdit = () => {
        latestIsEditingRef.current = false;
        setState({isEditing: false});
    };

    const renderName = (campaign: Campaign) => {
        return <StateLabel item={campaign} title={util.getErrorMessage(campaign)}>{campaign.name}</StateLabel>;
    };

    const sumHardCosts = () => {
        return util.formatCurrency(getSortedCampaigns()
                                       .reduce((t, c) => t + getHardCosts(c), 0));
    };

    const sumRecurringCosts = () => {
        return util.formatCurrency(getSortedCampaigns()
                                       .reduce((t, c) => t + getRecurringCosts(c), 0));
    };

    const sumLaborCosts = () => {
        return util.formatCurrency(getSortedCampaigns()
                                       .reduce((t, c) => t + getLaborCosts(c), 0));
    };

    const sumTotalCosts = () => {
        return util.formatCurrency(getSortedCampaigns()
                                       .reduce((t, c) => t + (getTotalCosts(c) as number), 0));
    };

    const sumNumLeads = () => {
        return <div style={{width: '4em'}}>
            <div style={{width: 'fit-content', margin: 'auto'}}>
                {mathUtils.sum(getSortedCampaigns().map(c => renderNumLeads(c)))}
            </div>
        </div>;
    };

    const rowStyleFn = (campaign: Campaign): CSSProperties => {
        return campaign.closed ? {backgroundColor: chroma('gray').alpha(.3).hex("auto")} : {} as CSSProperties;
    };

    const userId = getCurrentUserId();
    const isAdmin = Auth.isAdmin(userId);
    const onAddFn = isAdmin ? onAdd : null;
    const onDeleteFn = isAdmin ? onDelete : null;

    const campaigns = getSortedCampaigns().filter(c => c.entityType === 'campaign');
    const allHidden = (campaigns.length === 0) && (getCampaigns().length > 0);

    const colors = chroma.brewer.YlGnBu.map((color) => chroma.mix(color, 'white', .5).name());

    const CampaignsTable = EditableTable as unknown as React.ComponentType<EditableTableProps<Campaign>>;

    if (allHidden) return <div className='campaigns'>
        <MarketingPageHead
            title="Campaigns"
            subtitle="Track your marketing campaigns and the leads they bring in."
        />
        <div className='page__all-hidden'>
            {/* All campaigns hidden warning */}
            <div className='page__all-hidden-message'>
                <div>All campaigns are hidden due to filters</div>
            </div>
        </div>
    </div>;

    else return <div className='campaigns'>
        <MarketingPageHead
            title="Campaigns"
            subtitle="Track your marketing campaigns and the leads they bring in."
        />
        <div className='campaigns__table'>
        <Suspense fallback={<Loading/>}>
            <CampaignsTable
                entityName='campaign'
                name={`Campaigns${util.getPageRoot()?.replaceAll('/', '-')}`}
                urlPrefix="/marketing/campaigns"
                items={campaigns}
                rowStyleFn={rowStyleFn}
                groups={[
                    {
                        name: 'Basic Info',
                        backgroundColor: colors[0],
                        foregroundColor: 'black',
                        help: i18n.d('campaign', 'basicInfo')
                    },
                    isSmall() ? null : {
                        name: 'Costs',
                        backgroundColor: colors[1],
                        foregroundColor: 'black',
                        help: i18n.d('campaign', 'costs')
                    },
                    {
                        name: 'KPIs',
                        backgroundColor: colors[2],
                        foregroundColor: 'black',
                        help: i18n.d('campaign', 'kpis')
                    },
                    {
                        name: 'ROI',
                        backgroundColor: colors[3],
                        foregroundColor: 'black',
                        help: i18n.d('campaign', 'roi')
                    }
                ].filter(util.existsFilter)}
                properties={[
                    {
                        label: 'icon',
                        width: 'max-content',
                        justify: 'center',
                        hideLabel: true,
                        renderItemFn: getIcon,
                        group: 'Basic Info'
                    },
                    {
                        label: 'name',
                        width: '1fr',
                        renderItemFn: renderName,
                        group: 'Basic Info'
                    },
                    isSmall() ? null : {
                        label: 'category',
                        width: '1fr',
                        group: 'Basic Info'
                    },
                    isSmall() ? null : {
                        label: 'hardCosts',
                        displayLabel: 'Hard',
                        width: 'max-content',
                        justify: 'right',
                        renderItemFn: renderHardCosts,
                        group: 'Costs'
                    },
                    isSmall() ? null : {
                        label: 'recurringCosts',
                        displayLabel: 'Recurring',
                        width: 'max-content',
                        justify: 'right',
                        renderItemFn: renderRecurringCosts,
                        group: 'Costs'
                    },
                    isSmall() ? null : {
                        label: 'laborCosts',
                        displayLabel: 'Labor',
                        width: 'max-content',
                        justify: 'right',
                        renderItemFn: renderLaborCosts,
                        group: 'Costs'
                    },
                    isSmall() ? null : {
                        label: 'totalCost',
                        displayLabel: 'Total',
                        justify: 'right',
                        width: 'max-content',
                        renderItemFn: renderTotalCosts,
                        group: 'Costs'
                    },
                    {
                        label: 'numLeads',
                        displayLabel: isSmall() ? 'Num' : 'Leads',
                        justify: 'center',
                        width: 'max-content',
                        renderItemFn: renderNumLeads,
                        group: 'KPIs'
                    }, {
                        label: 'cpl',
                        displayLabel: isSmall() ? 'CPL' : '$/Lead',
                        justify: 'right',
                        width: 'max-content',
                        renderItemFn: renderCPL,
                        group: 'KPIs'
                    },
                    {
                        label: 'conversionRate',
                        displayLabel: isSmall() ? 'CR' : 'Conv %',
                        justify: 'right',
                        width: 'max-content',
                        renderItemFn: renderConversionRate,
                        group: 'KPIs'
                    },
                    {
                        label: 'billedROI',
                        displayLabel: 'Billed',
                        justify: isSmall() ? 'left' : undefined,
                        width: 'max-content',
                        renderItemFn: (campaign: Campaign) => renderROI(campaign, 'B'),
                        group: 'ROI'
                    },
                    {
                        label: 'collectedROI',
                        displayLabel: 'Collected',
                        justify: isSmall() ? 'left' : undefined,
                        width: 'max-content',
                        renderItemFn: (campaign: Campaign) => renderROI(campaign, 'C'),
                        group: 'ROI'
                    }
                ].filter(util.existsFilter) as EditableTablePropertyEntry<Campaign>[]}
                exportProps={[
                    {label: 'hardCosts', exportFn: getHardCosts, type: 'number'},
                    {label: 'recurringCosts', exportFn: getRecurringCosts, type: 'number'},
                    {label: 'laborCosts', exportFn: getLaborCosts, type: 'number'},
                    {label: 'totalCost', exportFn: getTotalCosts, type: 'number'},
                    {label: 'numLeads', exportFn: renderNumLeads, type: 'number'},
                    {
                        label: 'billedROI',
                        displayLabel: 'Billed ROI',
                        exportFn: c => getRoi(c, 'B'),
                        type: 'string'
                    },
                    {
                        label: 'collectedROI',
                        displayLabel: 'Collected ROI',
                        exportFn: c => getRoi(c, 'C'),
                        type: 'string'
                    },
                    {label: 'cpl', exportFn: getCostPerLead, type: 'number'},
                    {label: 'conversionRate', exportFn: getConversionRate, type: 'number'},
                ]}
                sortProps={[
                    {label: 'name', type: 'string', ignoreCase: true},
                    {label: 'category', type: 'string'},
                    {label: 'hardCosts', type: 'money', useFormatted: true},
                    {label: 'recurringCosts', type: 'money', useFormatted: true},
                    {label: 'laborCosts', type: 'money', useFormatted: true},
                    {label: 'totalCost', type: 'money', useFormatted: true},
                    {label: 'numLeads', type: 'number', useFormatted: true},
                    {label: 'cpl', type: 'money', useFormatted: true},
                    {label: 'conversionRate', type: 'number', useFormatted: true},
                    {label: 'billedROI', type: 'number', sortFn: (a, b) => sortRoi(a, b, 'B')},
                    {label: 'collectedROI', type: 'number', sortFn: (a, b) => sortRoi(a, b, 'C')},
                ]}
                sumProps={[
                    {label: 'hardCosts', type: 'money', renderItemFn: sumHardCosts},
                    {label: 'recurringCosts', type: 'money', renderItemFn: sumRecurringCosts},
                    {label: 'laborCosts', type: 'money', renderItemFn: sumLaborCosts},
                    {label: 'totalCost', type: 'money', renderItemFn: sumTotalCosts},
                    {label: 'numLeads', type: 'number', renderItemFn: sumNumLeads},
                ].filter(util.existsFilter) as EditableTableSumEntry<Campaign>[]}
                uniqueKey='name'
                env={{
                    getIconLabel: getIconLabel
                }}
                hideGridOnEdit={true}
                editor={CampaignCard}
                onAdd={onAddFn}
                onEdit={onEdit}
                onDelete={onDeleteFn}
                canDeleteFn={() => Auth.isAdmin(getCurrentUserId()) && cascadeReady}
                confirmDeleteWarningFn={confirmDeleteWarning}
                showReadOnlyIcon={false}
                onBeginEdit={onBeginEdit}
                onEndEdit={onEndEdit}
            />
        </Suspense>
        </div>
    </div>;
};

export type CampaignsProps = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    ContentBlockChildProps & WithRouter & {
    // Location object from navigation
    location?: string,

    // For searching
    searchTerms?: string[],
    searchOptions: SearchOptions,
}

const mapStateToProps = (state: RootState) => {
    return {
        campaigns: state.campaigns,
        categories: state.categories,
        contacts: state.contacts,
        documents: state.documents,
        tasks: state.tasks,
        users: state.users
    };
};

const mapDispatchToProps = (dispatch: RootDispatch) => {
    return {
        onSaveCampaignQueue: (campaign: Campaign) => dispatch(CampaignActions.saveCampaignQueue(campaign))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Campaigns as any));
